/**
 * Hikari Vault — Commercial App
 * =============================
 * Public-facing landing + legal/security pages. When the visitor decides to
 * use the product (click "Open vault", "Create your vault", or any CTA),
 * we hand off to the personal app flow which is the actual vault UX.
 *
 * Routing is intentionally simple state-based (no react-router) because we
 * only have a handful of pages and want zero new deps in this stage.
 *
 *   landing   → marketing site (long-scroll, multi-section)
 *   privacy   → privacy policy
 *   terms     → terms of service
 *   security  → security model deep-dive
 *   vault     → handoff to the personal vault flow
 */
import { useCallback, useEffect, useState } from "react";
import PersonalApp from "@hikari/app-personal";
import { C } from "@hikari/vault-core";

import Banner   from "./components/Banner";
import Header   from "./components/Header";
import Footer   from "./components/Footer";
import Landing  from "./pages/Landing";
import Privacy  from "./pages/Privacy";
import Terms    from "./pages/Terms";
import Security from "./pages/Security";

const VALID_VIEWS = new Set(["landing", "privacy", "terms", "security", "vault"]);

export default function CommercialApp() {
    const [view, setView] = useState(() => {
        const hash = (window.location.hash || "").replace("#", "");
        return VALID_VIEWS.has(hash) ? hash : "landing";
    });

    // Reflect view in the URL hash for shareable links + back button support
    useEffect(() => {
        if (view === "landing") {
            if (window.location.hash) {
                history.replaceState(null, "", window.location.pathname + window.location.search);
            }
        } else {
            window.location.hash = view;
        }
    }, [view]);

    useEffect(() => {
        const onHash = () => {
            const hash = (window.location.hash || "").replace("#", "");
            if (VALID_VIEWS.has(hash)) setView(hash);
            else if (!hash) setView("landing");
        };
        window.addEventListener("hashchange", onHash);
        return () => window.removeEventListener("hashchange", onHash);
    }, []);

    const handleNav = useCallback((next, anchor) => {
        setView(VALID_VIEWS.has(next) ? next : "landing");
        // Scroll-to-anchor on landing if requested
        if (next === "landing" && anchor) {
            setTimeout(() => {
                const el = document.getElementById(anchor);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 60);
        } else {
            window.scrollTo({ top: 0, behavior: "instant" });
        }
    }, []);

    const handleEnter = useCallback(() => {
        setView("vault");
    }, []);

    // Vault flow is full-screen with no chrome — it's the actual app
    if (view === "vault") {
        return <PersonalApp />;
    }

    return (
        <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: C.font }}>
            <Banner />
            <Header view={view} onNav={handleNav} onEnter={handleEnter} />
            {view === "landing"  && <Landing  onEnter={handleEnter} onNav={handleNav} />}
            {view === "privacy"  && <Privacy />}
            {view === "terms"    && <Terms />}
            {view === "security" && <Security />}
            <Footer onNav={handleNav} />
        </div>
    );
}
